import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {CropAspectLock, CropFrames} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';

export interface MediaCropModeState {
  isActive: boolean;
  itemId: string | null;
  /** The page the cropped item is on; only that page's controls layer draws the crop frame. */
  pageHashedId: string | null;
  /**
   * Locked aspect ratio for the crop frame. `null` means freeform (no lock).
   */
  aspectRatio: number | null;
  /** "Original" set the lock; a square picture's own ratio is also Square's, so the surfaces need this to light the right tile. */
  isOriginalAspectRatio: boolean;
  /**
   * The natural width/height ratio of the image being cropped, used for the "Original" preset.
   */
  originalAspectRatio: number | null;
  /**
   * Crop-frame rotation in degrees, normalized to the slider's -180..180 range.
   */
  rotation: number;
  /**
   * The window is the page and cannot be resized or moved: a page background is being cropped. The
   * surfaces then offer no window handles and no ratio presets, only the picture's own controls.
   */
  isWindowFixed: boolean;
  /**
   * The crop window's and the full image's on-screen boxes, republished by PosterMediaCrop on every
   * change so the React overlay can sit on them. `null` whenever nothing is on the canvas.
   */
  frames: CropFrames | null;
}

const initialState: MediaCropModeState = {
  isActive: false,
  itemId: null,
  pageHashedId: null,
  aspectRatio: null,
  isOriginalAspectRatio: false,
  originalAspectRatio: null,
  rotation: 0,
  isWindowFixed: false,
  frames: null,
};

const mediaCropMode = createSlice({
  name: 'mediaCropMode',
  initialState,
  reducers: {
    enterMediaCropMode: (
      state: MediaCropModeState,
      action: PayloadAction<{itemId: string; pageHashedId: string; aspectRatio: number | null; originalAspectRatio: number | null; rotation: number; isWindowFixed: boolean}>
    ) => {
      state.isActive = true;
      state.itemId = action.payload.itemId;
      state.pageHashedId = action.payload.pageHashedId;
      state.aspectRatio = action.payload.aspectRatio;
      state.isOriginalAspectRatio = false;
      state.originalAspectRatio = action.payload.originalAspectRatio;
      state.rotation = action.payload.rotation;
      state.isWindowFixed = action.payload.isWindowFixed;
    },
    setMediaCropAspectRatio: (state: MediaCropModeState, action: PayloadAction<CropAspectLock>) => {
      state.aspectRatio = action.payload.ratio;
      state.isOriginalAspectRatio = action.payload.isOriginal;
    },
    setMediaCropRotation: (state: MediaCropModeState, action: PayloadAction<number>) => {
      state.rotation = action.payload;
    },
    setMediaCropFrames: (state: MediaCropModeState, action: PayloadAction<CropFrames | null>) => {
      state.frames = action.payload;
    },
    exitMediaCropMode: (state: MediaCropModeState) => {
      state.isActive = false;
      state.itemId = null;
      state.pageHashedId = null;
      state.aspectRatio = null;
      state.isOriginalAspectRatio = false;
      state.originalAspectRatio = null;
      state.rotation = 0;
      state.isWindowFixed = false;
      state.frames = null;
    },
  },
});

export const {enterMediaCropMode, setMediaCropAspectRatio, setMediaCropRotation, setMediaCropFrames, exitMediaCropMode} = mediaCropMode.actions;
export const mediaCropModeReducer = mediaCropMode.reducer;
